// src/components/WhatsAppVinculo/WhatsAppVinculo.jsx
// Vincula el WhatsApp del usuario con el asistente de OddisMaps: genera un
// código de 6 dígitos que el usuario envía al bot desde su teléfono.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { API_BASE_URL } from '../../utils/config';

const WhatsAppVinculo = ({ userId }) => {
    const [estado, setEstado] = useState(null);
    const [codigo, setCodigo] = useState(null);
    const [cargando, setCargando] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}whatsapp/vinculo`, { params: { idUsuario: userId } });
            setEstado(data);
        } catch {
            setEstado(null);
        }
    }, [userId]);

    useEffect(() => { cargar(); }, [cargar]);

    // Mientras hay un código a la vista, se revisa si ya quedó vinculado.
    useEffect(() => {
        if (!codigo) return undefined;
        const id = setInterval(cargar, 5000);
        return () => clearInterval(id);
    }, [codigo, cargar]);

    useEffect(() => {
        if (codigo && estado?.vinculado) {
            setCodigo(null);
            toast.success('WhatsApp vinculado.');
        }
    }, [estado?.vinculado, codigo]);

    const generar = async () => {
        setCargando(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}whatsapp/vinculo/codigo`, { idUsuario: userId });
            setCodigo(data);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'No se pudo generar el código.');
        } finally {
            setCargando(false);
        }
    };

    const desvincular = async () => {
        try {
            await axios.delete(`${API_BASE_URL}whatsapp/vinculo`, { data: { idUsuario: userId } });
            toast.info('WhatsApp desvinculado.');
            cargar();
        } catch {
            toast.error('No se pudo desvincular.');
        }
    };

    return (
        <Paper sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
                <WhatsAppIcon sx={{ color: '#25D366' }} />
                <Typography variant="h6">Asistente por WhatsApp</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Pregunta por tus análisis desde WhatsApp («¿cuál fue mi último análisis?», «mándame el PDF»)
                y recibe el resumen y el PDF cuando termine un análisis en segundo plano.
            </Typography>

            {estado?.vinculado && !codigo && (
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    <Typography variant="body2">
                        Vinculado: <strong>{estado.telefonos.join(', ')}</strong>
                        {estado.numeroBot ? ` · Asistente: ${estado.numeroBot}` : ''}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={generar} disabled={cargando}>Vincular otro número</Button>
                    <Button size="small" color="error" onClick={desvincular}>Desvincular</Button>
                </Stack>
            )}

            {!estado?.vinculado && !codigo && (
                <Button variant="contained" onClick={generar} disabled={cargando} startIcon={<WhatsAppIcon />}
                        sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1da851' } }}>
                    Vincular mi WhatsApp
                </Button>
            )}

            {codigo && (
                <Box sx={{ mt: 1, p: 2, borderRadius: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    {codigo.sandboxJoin && (
                        <Typography variant="body2" mb={1.5}>
                            <strong>Paso 1 (solo pruebas):</strong> envía <code>{codigo.sandboxJoin}</code> al {codigo.numeroBot}
                            {codigo.enlaceSandbox && <> · <a href={codigo.enlaceSandbox} target="_blank" rel="noreferrer">abrir WhatsApp</a></>}
                        </Typography>
                    )}
                    <Typography variant="body2">
                        <strong>{codigo.sandboxJoin ? 'Paso 2: e' : 'E'}nvía este código</strong> al {codigo.numeroBot} desde tu WhatsApp:
                    </Typography>
                    <Typography variant="h4" sx={{ letterSpacing: 6, fontWeight: 700, my: 1 }}>{codigo.codigo}</Typography>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                        {codigo.enlace && (
                            <Button size="small" variant="contained" href={codigo.enlace} target="_blank" rel="noreferrer"
                                    sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1da851' } }}>
                                Enviar código por WhatsApp
                            </Button>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            Vence en {codigo.venceEnMinutos} minutos. Esta pantalla se actualiza sola al vincular.
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Paper>
    );
};

export default WhatsAppVinculo;
